// src/design-system/DataTable.tsx
import { type Key, type ReactNode, useState } from "react";

import { Button } from "./Button";

import { EmptyState, ErrorState, Loading } from "./states";

export type Column<T> = {
  header: string;
  cell: (row: T) => ReactNode;
  className?: string;
  width?: number | string;
};

type Props<T> = {
  rows: T[] | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => Key;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  empty?: { title: string; hint?: string; action?: ReactNode };
  minWidth?: number;
  onRowClick?: (row: T) => void;
  caption?: string;
  /** ترقيم من جانب العميل (الباك إند يرجع القوائم كاملة). الافتراضي 20 صفاً للصفحة. */
  pageSize?: number;
};

/** الجدول القياسي: رأس ثابت اللون، وحالات التحميل والخطأ والفراغ موحّدة. */
export function DataTable<T>({ rows, columns, rowKey, loading, error, onRetry, empty, minWidth = 640, onRowClick, caption, pageSize = 20 }: Props<T>) {
  const [page, setPage] = useState(0);
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} onRetry={onRetry} />;
  if (!rows?.length) return <EmptyState title={empty?.title ?? "لا توجد بيانات"} hint={empty?.hint} action={empty?.action} />;
  const pages = Math.ceil(rows.length / pageSize);
  const current = Math.min(page, pages - 1);
  const visible = rows.slice(current * pageSize, (current + 1) * pageSize);
  return (
    <div className="overflow-auto">
      <table className="tbl" style={{ minWidth }}>
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr>{columns.map((c) => <th key={c.header} style={{ width: c.width }}>{c.header}</th>)}</tr>
        </thead>
        <tbody>
          {visible.map((row) => (
            <tr key={rowKey(row)} onClick={onRowClick ? () => onRowClick(row) : undefined} className={onRowClick ? "row-action" : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? (e) => { if ((e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) { e.preventDefault(); onRowClick(row); } } : undefined}
              style={{ cursor: onRowClick ? "pointer" : undefined }}>
              {columns.map((c) => <td key={c.header} className={c.className}>{c.cell(row)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {pages > 1 && (
        <div className="flex items-center justify-between border-t border-line px-4 py-2.5 text-[12.5px] text-muted-2">
          <span>{current * pageSize + 1}–{current * pageSize + visible.length} من {rows.length}</span>
          <div className="flex gap-2">
            <Button small disabled={current === 0} onClick={() => setPage(current - 1)}>السابق</Button>
            <Button small disabled={current >= pages - 1} onClick={() => setPage(current + 1)}>التالي</Button>
          </div>
        </div>
      )}
    </div>
  );
}
